import React from 'react';
import { useLanguage } from '../../i18n/LanguageContext';

// Qualitative labels the AI may return as amount values
const AMOUNT_LABELS_EN = ['high', 'low', 'medium', 'moderate', 'trace', 'present', 'absent', 'none', 'contains gluten', 'gluten-free', 'may contain gluten'];

const AMOUNT_TRANSLATIONS_HE = {
  high: 'גבוה',
  low: 'נמוך',
  medium: 'בינוני',
  moderate: 'בינוני',
  trace: 'עקבות',
  present: 'קיים',
  absent: 'אין',
  none: 'אין',
  'contains gluten': 'מכיל גלוטן',
  'gluten-free': 'ללא גלוטן',
  'may contain gluten': 'עלול להכיל גלוטן',
};

const translateAmount = (amount, isRtl) => {
  if (!isRtl || !amount) return amount;
  const key = amount.trim().toLowerCase();
  return AMOUNT_TRANSLATIONS_HE[key] ?? amount;
};

// Maps tag key → CSS modifier class (also controls badge color)
const TAG_STYLE = {
  gluten_free: 'tag--green',
  high_calorie: 'tag--red',
  high_protein: 'tag--blue',
  high_carbs: 'tag--amber',
  high_sugar: 'tag--red',
  dietary_fiber: 'tag--green',
  high_fat: 'tag--amber',
};

const MacroBar = ({ label, value, unit, percent, colorClass }) => (
  <div className="nutrition-macro">
    <div className="nutrition-macro-header">
      <span className="nutrition-macro-label">{label}</span>
      <span className="nutrition-macro-value">
        {value != null ? `${Math.round(value)}${unit}` : '—'}
      </span>
    </div>
    <div className="nutrition-macro-track">
      <div
        className={`nutrition-macro-fill ${colorClass}`}
        style={{ width: `${Math.min(percent, 100)}%` }}
      />
    </div>
  </div>
);

const NutritionPanel = ({ recipe }) => {
  const { lang, t } = useLanguage();
  const n = t.nutrition;
  const nutrition = recipe?.nutrition;
  const isRtl = lang === 'he';

  if (!nutrition) {
    return (
      <section className="nutrition-panel nutrition-panel--pending" dir={isRtl ? 'rtl' : undefined}>
        <h3>{n.panelTitle}</h3>
        <p className="nutrition-pending">{n.notAvailable}</p>
      </section>
    );
  }

  const tagLabels = n.tags ?? {};
  const tags = nutrition.nutritionTags ?? [];

  return (
    <section className="nutrition-panel" dir={isRtl ? 'rtl' : undefined}>
      <h3>{n.panelTitle}</h3>
      {/* Calories hero */}
      <div className="nutrition-calories-hero">
        <div className="nutrition-calories-meta">
          <span className="nutrition-calories-label">{n.calories}</span>
          <span className="nutrition-per-serving">{n.perServing}</span>
        </div>
        <span className="nutrition-calories-number">
          {nutrition.calories != null ? Math.round(nutrition.calories) : '—'}
        </span>
      </div>

      {/* Macro bars */}
      <div className="nutrition-macros">
        <MacroBar
          label={n.fat}
          value={nutrition.fat_g}
          unit="g"
          percent={(nutrition.fat_g ?? 0) / 0.65}
          colorClass="fill--amber"
        />
        <MacroBar
          label={n.protein}
          value={nutrition.protein_g}
          unit="g"
          percent={(nutrition.protein_g ?? 0) / 0.5}
          colorClass="fill--blue"
        />
        <MacroBar
          label={n.carbs}
          value={nutrition.carbs_g}
          unit="g"
          percent={(nutrition.carbs_g ?? 0) / 3}
          colorClass="fill--green"
        />
        <MacroBar
          label={n.sugar}
          value={nutrition.sugar_g}
          unit="g"
          percent={(nutrition.sugar_g ?? 0) / 0.5}
          colorClass="fill--red"
        />
        <MacroBar
          label={n.fiber}
          value={nutrition.fiber_g}
          unit="g"
          percent={(nutrition.fiber_g ?? 0) / 0.38}
          colorClass="fill--teal"
        />
        {nutrition.iron_mg != null && (
          <MacroBar
            label={n.iron}
            value={nutrition.iron_mg}
            unit="mg"
            percent={(nutrition.iron_mg / 18) * 100}
            colorClass="fill--purple"
          />
        )}
      </div>

      {/* Micro-nutrients */}
      {(nutrition.vitamins?.length > 0 || nutrition.other_nutrients?.length > 0) && (
        <div className="nutrition-micro">
          {nutrition.vitamins?.length > 0 && (
            <div className="nutrition-micro-group">
              <h4>{n.vitamins}</h4>
              <ul>
                {nutrition.vitamins.map((v) => (
                  <li key={v.name}>
                    <span>{v.name}</span>
                    <span>{translateAmount(v.amount, isRtl)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {nutrition.other_nutrients?.length > 0 && (
            <div className="nutrition-micro-group">
              <h4>{n.otherNutrients}</h4>
              <ul>
                {nutrition.other_nutrients.map((o) => (
                  <li key={o.name}>
                    <span>{o.name}</span>
                    <span>{translateAmount(o.amount, isRtl)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Nutrition tags */}
      {tags.length > 0 && (
        <div className="nutrition-tags" aria-label="Nutrition tags">
          {tags.map((tag) => (
            <span key={tag} className={`nutrition-tag ${TAG_STYLE[tag] ?? 'tag--grey'}`}>
              {tagLabels[tag] ?? tag}
            </span>
          ))}
        </div>
      )}

      <p className="nutrition-disclaimer">{n.analyzed}</p>
    </section>
  );
};

export default NutritionPanel;
